import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format, addDays, parse, startOfDay } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon, UtensilsCrossed, Users, Bell, Trash2, Clock, Compass, Car, Plane, Ship, PartyPopper, Home, CheckCircle, AlertTriangle, DollarSign, FileText, MapPin, Upload, X, Loader2, ClipboardList, CreditCard, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCabin, getMeals, createGuest, getActiveGuest, updateGuest, createReservation, getReservations, cancelReservation, getTours, getGuestTourReservations, createTourReservation, cancelTourReservation, uploadPassport } from "@/lib/api";
import { translations, getMealNameTranslation, getDietaryTranslation } from "@/lib/translations";
import LanguageToggle from "@/components/LanguageToggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function GuestReservation() {
  const { hotelId, cabinId } = useParams();
  const navigate = useNavigate();
  const guestFormRef = useRef(null);
  const reservationFormRef = useRef(null);
  const reservationsListRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [hotelInfo, setHotelInfo] = useState(null);
  const [cabinInfo, setCabinInfo] = useState(null);
  const [meals, setMeals] = useState({});
  const [guest, setGuest] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [language, setLanguage] = useState("en");
  const [activeTab, setActiveTab] = useState("guest-info");
  
  // Get translation helper
  const t = translations[language].reservation;
  const tCommon = translations[language].common;
  
  // Dietary options with translations
  const DIETARY_OPTIONS = [
    { id: "vegetarian", label: getDietaryTranslation("vegetarian", language) },
    { id: "vegan", label: getDietaryTranslation("vegan", language) },
    { id: "gluten-free", label: getDietaryTranslation("gluten-free", language) },
  ];
  
  // Form state
  const [guestName, setGuestName] = useState("");
  const [guestCount, setGuestCount] = useState(1);
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [dietaryCounts, setDietaryCounts] = useState({
    vegetarian: 0,
    vegan: 0,
    "gluten-free": 0
  });
  const [specialRequests, setSpecialRequests] = useState("");
  
  // Bird Brain Bungalows specific fields
  const [celebration, setCelebration] = useState("");
  const [customCelebration, setCustomCelebration] = useState("");
  const [arrivalMethod, setArrivalMethod] = useState("");
  // Vehicle
  const [vehicleArrivalTime, setVehicleArrivalTime] = useState("");
  // Sansa
  const [sansaLandingTime, setSansaLandingTime] = useState("");
  const [sansaRequestTaxi, setSansaRequestTaxi] = useState(false);
  const [sansaTaxiTime, setSansaTaxiTime] = useState("");
  // Boat Shuttle
  const [boatShuttleTime, setBoatShuttleTime] = useState("");
  const [arrivalTaxiRequested, setArrivalTaxiRequested] = useState(false);
  // Departure
  const [departureMethod, setDepartureMethod] = useState("");
  const [departureShuttleTime, setDepartureShuttleTime] = useState("");
  const [departureTaxiRequested, setDepartureTaxiRequested] = useState(false);
  const [departureTaxiTime, setDepartureTaxiTime] = useState("");
  // Sansa Departure
  const [departureSansaTime, setDepartureSansaTime] = useState("");
  const [departureSansaTaxiRequested, setDepartureSansaTaxiRequested] = useState(false);
  const [departureSansaTaxiTime, setDepartureSansaTaxiTime] = useState("");
  // Checkout time
  const [checkoutTime, setCheckoutTime] = useState("");
  
  // Reservation form state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMeals, setSelectedMeals] = useState([]);
  const [roomService, setRoomService] = useState(false);
  const [specialTimeRequest, setSpecialTimeRequest] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [arrivalCalendarOpen, setArrivalCalendarOpen] = useState(false);
  const [departureCalendarOpen, setDepartureCalendarOpen] = useState(false);
  
  // Tours state (BBB only)
  const [toursSubTab, setToursSubTab] = useState("my-tours");
  const [restaurantSubTab, setRestaurantSubTab] = useState("my-reservations");
  const [tours, setTours] = useState([]);
  const [tourReservations, setTourReservations] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [tourDate, setTourDate] = useState(null);
  const [tourCalendarOpen, setTourCalendarOpen] = useState(false);
  const [numParticipants, setNumParticipants] = useState(2);
  const [participantNames, setParticipantNames] = useState([""]);
  const [passportFiles, setPassportFiles] = useState([]);
  const [depositConfirmed, setDepositConfirmed] = useState(false);
  const [tourSpecialRequests, setTourSpecialRequests] = useState("");
  const [tourSubmitting, setTourSubmitting] = useState(false);
  const [uploadingPassport, setUploadingPassport] = useState(false);
  
  // Check if this is Bird Brain Bungalows
  const isBirdBrain = hotelId === "bird-brain-bungalows";

  useEffect(() => {
    loadData();
  }, [hotelId, cabinId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load cabin info
      const cabinRes = await getCabin(hotelId, cabinId);
      setHotelInfo(cabinRes.data.hotel);
      setCabinInfo(cabinRes.data.cabin);
      
      // Load meals
      const mealsRes = await getMeals();
      setMeals(mealsRes.data);
      
      // Check for existing guest
      const guestRes = await getActiveGuest(hotelId, cabinId);
      if (guestRes.data) {
        setGuest(guestRes.data);
        setGuestName(guestRes.data.guest_name);
        setGuestCount(guestRes.data.guest_count);
        if (guestRes.data.arrival_date) {
          setArrivalDate(parse(guestRes.data.arrival_date, "yyyy-MM-dd", new Date()));
        }
        setDepartureDate(parse(guestRes.data.departure_date, "yyyy-MM-dd", new Date()));
        
        // Handle dietary requirements - convert from old format if needed
        const dietaryReqs = guestRes.data.dietary_requirements || {};
        if (Array.isArray(dietaryReqs)) {
          // Old format - convert to new format
          const newFormat = { vegetarian: 0, vegan: 0, "gluten-free": 0 };
          dietaryReqs.forEach(d => { if (newFormat.hasOwnProperty(d)) newFormat[d] = 1; });
          setDietaryCounts(newFormat);
        } else {
          setDietaryCounts({
            vegetarian: dietaryReqs.vegetarian || 0,
            vegan: dietaryReqs.vegan || 0,
            "gluten-free": dietaryReqs["gluten-free"] || 0
          });
        }
        
        setSpecialRequests(guestRes.data.special_requests || "");
        
        // Load Bird Brain Bungalows specific fields
        const celebrationVal = guestRes.data.celebration || "";
        if (["birthday", "anniversary", "honeymoon"].includes(celebrationVal)) {
          setCelebration(celebrationVal);
          setCustomCelebration("");
        } else if (celebrationVal) {
          setCelebration("custom");
          setCustomCelebration(celebrationVal);
        }
        setArrivalMethod(guestRes.data.arrival_method || "");
        setVehicleArrivalTime(guestRes.data.vehicle_arrival_time || "");
        setSansaLandingTime(guestRes.data.sansa_landing_time || "");
        setSansaRequestTaxi(guestRes.data.sansa_request_taxi || false);
        setSansaTaxiTime(guestRes.data.sansa_taxi_time || "");
        setBoatShuttleTime(guestRes.data.boat_shuttle_time || "");
        setCheckoutTime(guestRes.data.checkout_time || "");
        // Load departure fields
        setDepartureMethod(guestRes.data.departure_method || "");
        setDepartureShuttleTime(guestRes.data.departure_shuttle_time || "");
        setDepartureTaxiRequested(guestRes.data.departure_taxi_requested || false);
        setDepartureTaxiTime(guestRes.data.departure_taxi_time || "");
        setDepartureSansaTime(guestRes.data.departure_sansa_time || "");
        setDepartureSansaTaxiRequested(guestRes.data.departure_sansa_taxi_requested || false);
        setDepartureSansaTaxiTime(guestRes.data.departure_sansa_taxi_time || "");
        
        // Load existing reservations
        const resRes = await getReservations({ guest_id: guestRes.data.id });
        setReservations(resRes.data);
        
        // Load tours for BBB guests
        if (hotelId === "bird-brain-bungalows") {
          const toursRes = await getTours();
          setTours(toursRes.data);
          const tourResRes = await getGuestTourReservations(guestRes.data.id);
          setTourReservations(tourResRes.data);
        }
      } else if (hotelId === "bird-brain-bungalows") {
        // Load tours even without guest registration
        const toursRes = await getTours();
        setTours(toursRes.data);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      if (error.response?.status === 404) {
        toast.error("Invalid cabin. Please scan the QR code again.");
        navigate("/");
      } else {
        toast.error("Failed to load reservation data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGuest = async () => {
    if (!guestName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!arrivalDate) {
      toast.error("Please select your arrival date");
      return;
    }
    if (!departureDate) {
      toast.error("Please select your departure date");
      return;
    }
    if (guestCount < 1) {
      toast.error("Guest count must be at least 1");
      return;
    }

    // Validate dietary counts don't exceed guest count
    const totalDietary = Object.values(dietaryCounts).reduce((sum, count) => sum + count, 0);
    if (totalDietary > guestCount) {
      toast.error("Total dietary requirements cannot exceed number of guests");
      return;
    }
    
    // Validate Bird Brain Bungalows specific fields
    if (isBirdBrain && arrivalMethod) {
      if (arrivalMethod === "vehicle" && !vehicleArrivalTime) {
        toast.error("Please select your estimated arrival time");
        return;
      }
      if (arrivalMethod === "sansa" && !sansaLandingTime) {
        toast.error("Please enter your Sansa flight landing time");
        return;
      }
      if (arrivalMethod === "boat_shuttle" && !boatShuttleTime) {
        toast.error("Please select your boat shuttle time");
        return;
      }
    }
    
    // Determine if vehicle arrival requires approval (before 2pm or 8pm onwards)
    let requiresApproval = false;
    if (arrivalMethod === "vehicle" && vehicleArrivalTime) {
      const hour = parseInt(vehicleArrivalTime.split(":")[0]);
      requiresApproval = hour < 14 || hour >= 20; // Before 2pm (14:00) or 8pm+ (20:00)
    }

    try {
      setSubmitting(true);
      const guestData = {
        hotel_id: hotelId,
        cabin_id: cabinId,
        guest_name: guestName.trim(),
        guest_count: guestCount,
        arrival_date: format(arrivalDate, "yyyy-MM-dd"),
        departure_date: format(departureDate, "yyyy-MM-dd"),
        dietary_requirements: dietaryCounts,
        special_requests: specialRequests.trim(),
        // Bird Brain Bungalows specific fields
        celebration: celebration === "custom" ? customCelebration.trim() : celebration,
        arrival_method: isBirdBrain ? arrivalMethod : "",
        vehicle_arrival_time: arrivalMethod === "vehicle" ? vehicleArrivalTime : "",
        vehicle_requires_approval: requiresApproval,
        sansa_landing_time: arrivalMethod === "sansa" ? sansaLandingTime : "",
        sansa_request_taxi: arrivalMethod === "sansa" ? sansaRequestTaxi : false,
        sansa_taxi_time: arrivalMethod === "sansa" && sansaRequestTaxi ? sansaTaxiTime : "",
        boat_shuttle_time: arrivalMethod === "boat_shuttle" ? boatShuttleTime : "",
        checkout_time: checkoutTime,
        // Departure fields
        departure_method: isBirdBrain ? departureMethod : "",
        departure_shuttle_time: departureMethod === "boat_shuttle" ? departureShuttleTime : "",
        departure_taxi_requested: departureMethod === "boat_shuttle" ? departureTaxiRequested : false,
        departure_taxi_time: departureMethod === "boat_shuttle" && departureTaxiRequested ? departureTaxiTime : "",
        departure_sansa_time: departureMethod === "sansa_flight" ? departureSansaTime : "",
        departure_sansa_taxi_requested: departureMethod === "sansa_flight" ? departureSansaTaxiRequested : false,
        departure_sansa_taxi_time: departureMethod === "sansa_flight" && departureSansaTaxiRequested ? departureSansaTaxiTime : "",
      };

      if (guest) {
        await updateGuest(guest.id, guestData);
        toast.success("Guest information updated");
      } else {
        const res = await createGuest(guestData);
        setGuest(res.data);
        toast.success("Guest registered successfully");
      }
      
      // Reload data
      await loadData();
      
      // Scroll to reservation form after data loads
      setTimeout(() => {
        if (reservationFormRef.current) {
          const yOffset = -20; // Small offset from top
          const element = reservationFormRef.current;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 500);
    } catch (error) {
      console.error("Error saving guest:", error);
      toast.error(error.response?.data?.detail || "Failed to save guest information");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMakeReservation = async () => {
    if (!guest) {
      toast.error("Please register your information first");
      return;
    }
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }
    if (selectedMeals.length === 0) {
      toast.error("Please select at least one meal");
      return;
    }

    try {
      setSubmitting(true);
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      
      for (const mealType of selectedMeals) {
        await createReservation({
          guest_id: guest.id,
          meal_type: mealType,
          date: dateStr,
          room_service: roomService,
          special_time_request: specialTimeRequest.trim(),
        });
      }
      
      toast.success("Reservations created successfully!");
      setSelectedMeals([]);
      setRoomService(false);
      setSpecialTimeRequest("");
      
      // Reload reservations
      const resRes = await getReservations({ guest_id: guest.id });
      setReservations(resRes.data);
      
      // Scroll to reservations list to show confirmed reservations
      setTimeout(() => {
        if (reservationsListRef.current) {
          const yOffset = -20;
          const element = reservationsListRef.current;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 300);
    } catch (error) {
      console.error("Error creating reservation:", error);
      toast.error(error.response?.data?.detail || "Failed to create reservation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelReservation = async (reservationId) => {
    try {
      await cancelReservation(reservationId);
      toast.success("Reservation cancelled");
      
      // Reload reservations
      const resRes = await getReservations({ guest_id: guest.id });
      setReservations(resRes.data);
    } catch (error) {
      console.error("Error cancelling reservation:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel reservation");
    }
  };

  const updateDietaryCount = (id, value) => {
    if (value === '') {
      setDietaryCounts(prev => ({ ...prev, [id]: '' }));
    } else {
      const numValue = Math.max(0, Math.min(guestCount || 1, parseInt(value) || 0));
      setDietaryCounts(prev => ({ ...prev, [id]: numValue }));
    }
  };

  const handleDietaryBlur = (id, value) => {
    if (value === '' || isNaN(parseInt(value))) {
      setDietaryCounts(prev => ({ ...prev, [id]: 0 }));
    }
  };

  const toggleMeal = (mealType) => {
    setSelectedMeals(prev =>
      prev.includes(mealType) ? prev.filter(m => m !== mealType) : [...prev, mealType]
    );
  };

  const calculatePrice = () => {
    let total = 0;
    for (const mealType of selectedMeals) {
      const meal = meals[mealType];
      if (meal) {
        total += meal.price * guestCount;
        if (roomService) {
          total += 2.50 * guestCount;
        }
      }
    }
    return total;
  };

  const formatDietaryDisplay = (dietary) => {
    if (!dietary) return null;
    
    // Handle both old array format and new object format
    if (Array.isArray(dietary)) {
      return dietary.length > 0 ? dietary.join(", ") : null;
    }
    
    const items = [];
    Object.entries(dietary).forEach(([key, count]) => {
      if (count > 0) {
        items.push(`${count} ${getDietaryTranslation(key, language)}`);
      }
    });
    return items.length > 0 ? items.join(", ") : null;
  };

  // ==================== TOUR HANDLERS ====================
  
  // Update participant names array when count changes
  useEffect(() => {
    setParticipantNames(prev => {
      const newNames = [...prev];
      while (newNames.length < numParticipants) {
        newNames.push("");
      }
      return newNames.slice(0, numParticipants);
    });
  }, [numParticipants]);

  const handlePassportUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    try {
      setUploadingPassport(true);
      const newFiles = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('guest_id', guest.id);
        
        const res = await uploadPassport(formData);
        newFiles.push(res.data.filename);
      }
      
      setPassportFiles(prev => [...prev, ...newFiles]);
      toast.success(`${newFiles.length} passport(s) uploaded`);
    } catch (error) {
      console.error("Error uploading passport:", error);
      toast.error("Failed to upload passport");
    } finally {
      setUploadingPassport(false);
      e.target.value = '';
    }
  };

  const removePassport = (index) => {
    setPassportFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleBookTour = async () => {
    if (!selectedTour) {
      toast.error("Please select a tour");
      return;
    }
    if (!tourDate) {
      toast.error("Please select a date");
      return;
    }
    if (numParticipants < 1 || numParticipants > 6) {
      toast.error("Please select between 1-6 participants");
      return;
    }
    if (selectedTour.requires_passport && passportFiles.length < numParticipants) {
      toast.error(`Please upload passport for all ${numParticipants} participants`);
      return;
    }
    if (selectedTour.requires_certification && passportFiles.length < numParticipants) {
      toast.error(`Please upload dive certification for all ${numParticipants} participants`);
      return;
    }
    if (selectedTour.deposit_required && !depositConfirmed) {
      toast.error("Please confirm you have paid the deposit via PayPal");
      return;
    }

    try {
      setTourSubmitting(true);
      
      await createTourReservation({
        guest_id: guest.id,
        tour_id: selectedTour.id,
        date: format(tourDate, "yyyy-MM-dd"),
        num_participants: numParticipants,
        participant_names: participantNames.filter(n => n.trim()),
        passport_files: passportFiles,
        deposit_confirmed: depositConfirmed,
        special_requests: tourSpecialRequests.trim()
      });
      
      toast.success(`${selectedTour.name} booked! Select another tour or view My Tours.`);
      
      // Reset form but stay on Available Tours
      setSelectedTour(null);
      setTourDate(null);
      setNumParticipants(2);
      setParticipantNames([""]);
      setPassportFiles([]);
      setDepositConfirmed(false);
      setTourSpecialRequests("");
      
      // Reload tour reservations
      const tourResRes = await getGuestTourReservations(guest.id);
      setTourReservations(tourResRes.data);
      
    } catch (error) {
      console.error("Error booking tour:", error);
      toast.error(error.response?.data?.detail || "Failed to book tour");
    } finally {
      setTourSubmitting(false);
    }
  };

  const handleCancelTour = async (reservationId) => {
    if (!confirm("Are you sure you want to cancel this tour? Note: Any deposit paid is non-refundable.")) {
      return;
    }
    
    try {
      await cancelTourReservation(reservationId);
      toast.success("Tour reservation cancelled");
      
      // Reload reservations
      const tourResRes = await getGuestTourReservations(guest.id);
      setTourReservations(tourResRes.data);
    } catch (error) {
      console.error("Error cancelling tour:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel tour");
    }
  };

  const calculateTourPrice = () => {
    if (!selectedTour) return 0;
    return selectedTour.price * numParticipants;
  };

  const getTourStatusBadge = (status) => {
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
      confirmed: "bg-green-100 text-green-800 border-green-300",
      rejected: "bg-red-100 text-red-800 border-red-300",
      cancelled: "bg-gray-100 text-gray-800 border-gray-300"
    };
    return statusStyles[status] || statusStyles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <UtensilsCrossed className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">{tCommon.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background texture-overlay">
      <div className="relative z-10 max-w-2xl mx-auto px-4 py-8">
        {/* Language Toggle */}
        <div className="flex justify-end mb-4">
          <LanguageToggle language={language} setLanguage={setLanguage} />
        </div>

        {/* Header - Hotel/Cabin focused */}
        <div className="text-center mb-6">
          <div className="flex flex-col items-center bg-primary/10 px-6 py-5 rounded-xl border border-primary/20 mb-4 w-full">
            <Home className="w-7 h-7 text-primary mb-2" />
            <span className="text-primary font-display text-2xl sm:text-3xl font-bold" data-testid="hotel-name">
              {hotelInfo?.name}
            </span>
            <span className="text-primary font-display text-xl sm:text-2xl mt-1 font-bold" data-testid="cabin-name">
              {cabinInfo?.name}
            </span>
          </div>
          {guest && (
            <p className="text-muted-foreground">
              Welcome, <span className="font-medium text-foreground">{guest.guest_name}</span>
            </p>
          )}
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={cn(
            "grid w-full mb-6 h-auto p-2 gap-2 bg-card/80 backdrop-blur-sm border border-border rounded-xl shadow-sm",
            isBirdBrain ? "grid-cols-4" : "grid-cols-3"
          )}>
            <TabsTrigger 
              value="guest-info" 
              className={cn(
                "flex items-center justify-center gap-2 py-3 px-2 sm:px-4 rounded-lg font-medium transition-all duration-200",
                "hover:bg-primary/10 hover:scale-[1.02]",
                "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=active]:scale-[1.02]",
                "data-[state=inactive]:text-muted-foreground"
              )}
              data-testid="tab-guest-info"
            >
              <Users className="w-5 h-5" />
              <span className="text-xs sm:text-base">{translations[language].tabs.guestInfo}</span>
            </TabsTrigger>
            {isBirdBrain && (
              <TabsTrigger 
                value="tours" 
                className={cn(
                  "flex items-center justify-center gap-2 py-3 px-2 sm:px-4 rounded-lg font-medium transition-all duration-200",
                  "hover:bg-primary/10 hover:scale-[1.02]",
                  "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=active]:scale-[1.02]",
                  "data-[state=inactive]:text-muted-foreground"
                )}
                data-testid="tab-tours"
              >
                <Compass className="w-5 h-5" />
                <span className="text-xs sm:text-base">{translations[language].tabs.tours}</span>
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="restaurant" 
              className={cn(
                "flex items-center justify-center gap-2 py-3 px-2 sm:px-4 rounded-lg font-medium transition-all duration-200",
                "hover:bg-primary/10 hover:scale-[1.02]",
                "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=active]:scale-[1.02]",
                "data-[state=inactive]:text-muted-foreground"
              )}
              data-testid="tab-restaurant"
            >
              <UtensilsCrossed className="w-5 h-5" />
              <span className="text-xs sm:text-base">{translations[language].tabs.restaurant}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="payment" 
              className={cn(
                "flex items-center justify-center gap-2 py-3 px-2 sm:px-4 rounded-lg font-medium transition-all duration-200",
                "hover:bg-primary/10 hover:scale-[1.02]",
                "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=active]:scale-[1.02]",
                "data-[state=inactive]:text-muted-foreground"
              )}
              data-testid="tab-payment"
            >
              <CreditCard className="w-5 h-5" />
              <span className="text-xs sm:text-base">{translations[language].tabs.payment}</span>
            </TabsTrigger>
          </TabsList>

          {/* ==================== GUEST INFO TAB ==================== */}
          <TabsContent value="guest-info" className="space-y-6">
            <Card ref={guestFormRef} className="bg-card border-border shadow-card" data-testid="guest-info-card">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  {t.guestInfo}
                </CardTitle>
                <CardDescription>
                  {guest ? t.updateIfNeeded : t.registerToMake}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="guestName">{t.guestName}</Label>
                    <Input
                      id="guestName"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder={t.guestNamePlaceholder}
                      className="bg-background/50"
                      data-testid="guest-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guestCount">{t.numberOfGuests}</Label>
                    <Input
                      id="guestCount"
                      type="number"
                      min={1}
                      value={guestCount}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          setGuestCount('');
                        } else {
                          setGuestCount(Math.max(1, parseInt(val) || 1));
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '' || parseInt(e.target.value) < 1) {
                          setGuestCount(1);
                        }
                      }}
                      className="bg-background/50"
                      data-testid="guest-count-input"
                    />
                  </div>
                </div>

                {/* Arrival and Departure Dates */}
                <div className="flex gap-3">
                  <div className="flex-1 space-y-2">
                    <Label>{t.arrivalDate || "Arrival Date"}</Label>
                    <Popover open={arrivalCalendarOpen} onOpenChange={setArrivalCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal bg-background/50",
                            !arrivalDate && "text-muted-foreground"
                          )}
                          data-testid="arrival-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {arrivalDate ? format(arrivalDate, "PPP", { locale: language === "es" ? es : undefined }) : t.selectDate}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-card border-border">
                        <Calendar
                          mode="single"
                          selected={arrivalDate}
                          onSelect={setArrivalDate}
                          disabled={(date) => date < startOfDay(new Date())}
                          initialFocus
                        />
                        <div className="p-3 border-t border-border">
                          <Button 
                            className="w-full bg-primary hover:bg-primary/90"
                            onClick={() => setArrivalCalendarOpen(false)}
                            disabled={!arrivalDate}
                          >
                            {tCommon.ok}
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label>{t.departureDate}</Label>
                    <Popover open={departureCalendarOpen} onOpenChange={setDepartureCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal bg-background/50",
                            !departureDate && "text-muted-foreground"
                          )}
                          data-testid="departure-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {departureDate ? format(departureDate, "PPP", { locale: language === "es" ? es : undefined }) : t.selectDate}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-card border-border">
                        <Calendar
                          mode="single"
                          selected={departureDate}
                          onSelect={setDepartureDate}
                          disabled={(date) => date < startOfDay(new Date()) || (arrivalDate && date < arrivalDate)}
                          initialFocus
                          data-testid="departure-calendar"
                        />
                        <div className="p-3 border-t border-border">
                          <Button 
                            className="w-full bg-primary hover:bg-primary/90"
                            onClick={() => setDepartureCalendarOpen(false)}
                            disabled={!departureDate}
                          >
                            {tCommon.ok}
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Bird Brain Bungalows Specific Fields */}
                {isBirdBrain && (
                  <>
                    {/* Celebration */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <PartyPopper className="w-4 h-4 text-primary" />
                        Celebrating? (Optional)
                      </Label>
                      <Select value={celebration} onValueChange={setCelebration}>
                        <SelectTrigger className="bg-background/50" data-testid="celebration-select">
                          <SelectValue placeholder="Select if applicable" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No celebration</SelectItem>
                          <SelectItem value="birthday">Birthday</SelectItem>
                          <SelectItem value="anniversary">Anniversary</SelectItem>
                          <SelectItem value="honeymoon">Honeymoon</SelectItem>
                          <SelectItem value="custom">Other (specify)</SelectItem>
                        </SelectContent>
                      </Select>
                      {celebration === "custom" && (
                        <Input
                          value={customCelebration}
                          onChange={(e) => setCustomCelebration(e.target.value)}
                          placeholder="What are you celebrating?"
                          className="bg-background/50"
                          data-testid="custom-celebration-input"
                        />
                      )}
                    </div>

                    {/* Arrival Method */}
                    <div className="space-y-3">
                      <Label className="text-base font-semibold">How will you arrive?</Label>
                      <div className="grid grid-cols-3 gap-2 sm:gap-3">
                        <div
                          onClick={() => setArrivalMethod("vehicle")}
                          className={cn(
                            "flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 rounded-lg border cursor-pointer transition-all",
                            arrivalMethod === "vehicle"
                              ? "bg-primary/20 border-primary shadow-glow"
                              : "bg-background/50 border-border hover:border-primary/50"
                          )}
                          data-testid="arrival-vehicle"
                        >
                          <Car className="w-5 h-5 sm:w-6 sm:h-6" />
                          <span className="text-xs sm:text-sm font-medium text-center">Vehicle</span>
                        </div>
                        <div
                          onClick={() => setArrivalMethod("sansa")}
                          className={cn(
                            "flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 rounded-lg border cursor-pointer transition-all",
                            arrivalMethod === "sansa"
                              ? "bg-primary/20 border-primary shadow-glow"
                              : "bg-background/50 border-border hover:border-primary/50"
                          )}
                          data-testid="arrival-sansa"
                        >
                          <Plane className="w-5 h-5 sm:w-6 sm:h-6" />
                          <span className="text-xs sm:text-sm font-medium text-center">Sansa Flight</span>
                        </div>
                        <div
                          onClick={() => setArrivalMethod("boat_shuttle")}
                          className={cn(
                            "flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 rounded-lg border cursor-pointer transition-all",
                            arrivalMethod === "boat_shuttle"
                              ? "bg-primary/20 border-primary shadow-glow"
                              : "bg-background/50 border-border hover:border-primary/50"
                          )}
                          data-testid="arrival-boat"
                        >
                          <Ship className="w-5 h-5 sm:w-6 sm:h-6" />
                          <span className="text-xs sm:text-sm font-medium text-center">Boat Shuttle</span>
                        </div>
                      </div>
                    </div>

                    {/* Vehicle Arrival Details */}
                    {arrivalMethod === "vehicle" && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                        <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                          <Car className="w-4 h-4" />
                          Vehicle Arrival Details
                        </h4>
                        <div className="space-y-2">
                          <Label>Estimated Arrival Time (11am - 10pm)</Label>
                          <Select value={vehicleArrivalTime} onValueChange={setVehicleArrivalTime}>
                            <SelectTrigger className="bg-white" data-testid="vehicle-time-select">
                              <SelectValue placeholder="Select arrival time" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="11:00">11:00 AM</SelectItem>
                              <SelectItem value="11:30">11:30 AM</SelectItem>
                              <SelectItem value="12:00">12:00 PM</SelectItem>
                              <SelectItem value="12:30">12:30 PM</SelectItem>
                              <SelectItem value="13:00">1:00 PM</SelectItem>
                              <SelectItem value="13:30">1:30 PM</SelectItem>
                              <SelectItem value="14:00">2:00 PM</SelectItem>
                              <SelectItem value="14:30">2:30 PM</SelectItem>
                              <SelectItem value="15:00">3:00 PM</SelectItem>
                              <SelectItem value="15:30">3:30 PM</SelectItem>
                              <SelectItem value="16:00">4:00 PM</SelectItem>
                              <SelectItem value="16:30">4:30 PM</SelectItem>
                              <SelectItem value="17:00">5:00 PM</SelectItem>
                              <SelectItem value="17:30">5:30 PM</SelectItem>
                              <SelectItem value="18:00">6:00 PM</SelectItem>
                              <SelectItem value="18:30">6:30 PM</SelectItem>
                              <SelectItem value="19:00">7:00 PM</SelectItem>
                              <SelectItem value="19:30">7:30 PM</SelectItem>
                              <SelectItem value="20:00">8:00 PM</SelectItem>
                              <SelectItem value="20:30">8:30 PM</SelectItem>
                              <SelectItem value="21:00">9:00 PM</SelectItem>
                              <SelectItem value="21:30">9:30 PM</SelectItem>
                              <SelectItem value="22:00">10:00 PM</SelectItem>
                            </SelectContent>
                          </Select>
                          {vehicleArrivalTime && (parseInt(vehicleArrivalTime.split(":")[0]) < 14 || parseInt(vehicleArrivalTime.split(":")[0]) >= 20) && (
                            <p className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded">
                              ⚠️ Arrivals before 2pm or after 8pm require admin approval. We'll confirm availability.
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Sansa Flight Arrival Details */}
                    {arrivalMethod === "sansa" && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                        <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                          <Plane className="w-4 h-4" />
                          Sansa Flight Details
                        </h4>
                        <div className="space-y-2">
                          <Label>Landing Time in Drake Bay</Label>
                          <Input
                            value={sansaLandingTime}
                            onChange={(e) => setSansaLandingTime(e.target.value)}
                            placeholder="e.g., 10:30 AM"
                            className="bg-white"
                            data-testid="sansa-landing-time"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <Checkbox
                              checked={sansaRequestTaxi}
                              onCheckedChange={setSansaRequestTaxi}
                              data-testid="sansa-taxi-checkbox"
                            />
                            <span className="text-sm">Request taxi from airport ($60, paid directly to driver)</span>
                          </label>
                          {sansaRequestTaxi && (
                            <>
                              <Input
                                value={sansaTaxiTime}
                                onChange={(e) => setSansaTaxiTime(e.target.value)}
                                placeholder="Preferred taxi pickup time"
                                className="bg-white"
                                data-testid="sansa-taxi-time"
                              />
                              <p className="text-xs text-blue-700">
                                We'll make the reservation. Taxi can stop at the supermarket along the way at no extra cost.
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Boat Shuttle Arrival Details */}
                    {arrivalMethod === "boat_shuttle" && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                        <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                          <Ship className="w-4 h-4" />
                          Boat Shuttle from Sierpe to Drake Bay
                        </h4>
                        <p className="text-sm text-blue-700">
                          Boat shuttles are $20/person paid directly to the boat captain. We'll make the reservation.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div
                            onClick={() => setBoatShuttleTime("11:30am")}
                            className={cn(
                              "p-4 rounded-lg border cursor-pointer transition-all text-center",
                              boatShuttleTime === "11:30am"
                                ? "bg-white border-blue-400 shadow-md"
                                : "bg-white/50 border-blue-200 hover:border-blue-300"
                            )}
                            data-testid="shuttle-1130"
                          >
                            <p className="font-semibold">11:30 AM</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Taxi at 12:30 PM in Drake Bay ($20)
                            </p>
                          </div>
                          <div
                            onClick={() => setBoatShuttleTime("4pm")}
                            className={cn(
                              "p-4 rounded-lg border cursor-pointer transition-all text-center",
                              boatShuttleTime === "4pm"
                                ? "bg-white border-blue-400 shadow-md"
                                : "bg-white/50 border-blue-200 hover:border-blue-300"
                            )}
                            data-testid="shuttle-4pm"
                          >
                            <p className="font-semibold">4:00 PM</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Taxi at 5:00 PM in Drake Bay ($20)
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-blue-700 mb-3">
                          A taxi will be waiting for you in Drake Bay. Cost is $30, paid directly to driver.
                        </p>
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
                          <input
                            type="checkbox"
                            id="arrival-taxi-checkbox"
                            checked={arrivalTaxiRequested}
                            onChange={(e) => setArrivalTaxiRequested(e.target.checked)}
                            className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                            data-testid="arrival-taxi-checkbox"
                          />
                          <label htmlFor="arrival-taxi-checkbox" className="text-sm text-blue-800">
                            Yes, please arrange a taxi for me from Drake Bay to the cabin
                          </label>
                        </div>
                      </div>
                    )}

                    {/* How will you depart? */}
                    <div className="space-y-4">
                      <Label className="text-base font-semibold flex items-center gap-2">
                        <Ship className="h-5 w-5 text-primary" />
                        How will you depart?
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Select your departure method from Drake Bay.
                      </p>
                      <div className="grid grid-cols-3 gap-2 sm:gap-3">
                        <div
                          onClick={() => setDepartureMethod("own_arrangements")}
                          className={cn(
                            "p-2 sm:p-4 rounded-lg border cursor-pointer transition-all text-center",
                            departureMethod === "own_arrangements"
                              ? "bg-white border-primary shadow-md"
                              : "bg-white/50 border-border hover:border-primary/50"
                          )}
                          data-testid="departure-own"
                        >
                          <Car className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-muted-foreground" />
                          <span className="text-xs sm:text-sm font-medium">Own Arrangements</span>
                        </div>
                        <div
                          onClick={() => setDepartureMethod("sansa_flight")}
                          className={cn(
                            "p-2 sm:p-4 rounded-lg border cursor-pointer transition-all text-center",
                            departureMethod === "sansa_flight"
                              ? "bg-white border-blue-400 shadow-md"
                              : "bg-white/50 border-border hover:border-blue-300"
                          )}
                          data-testid="departure-sansa"
                        >
                          <Plane className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-blue-600" />
                          <span className="text-xs sm:text-sm font-medium">Sansa Flight</span>
                        </div>
                        <div
                          onClick={() => setDepartureMethod("boat_shuttle")}
                          className={cn(
                            "p-2 sm:p-4 rounded-lg border cursor-pointer transition-all text-center",
                            departureMethod === "boat_shuttle"
                              ? "bg-white border-blue-400 shadow-md"
                              : "bg-white/50 border-border hover:border-blue-300"
                          )}
                          data-testid="departure-boat-shuttle"
                        >
                          <Ship className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-blue-600" />
                          <span className="text-xs sm:text-sm font-medium">Boat Shuttle</span>
                        </div>
                      </div>

                      {/* Sansa Flight Departure Details */}
                      {departureMethod === "sansa_flight" && (
                        <div className="p-4 rounded-lg border border-blue-200 bg-blue-50/50 space-y-4">
                          <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                            <Plane className="h-4 w-4" />
                            Sansa Flight Departure
                          </h4>
                          <div className="space-y-2">
                            <Label>Flight Departure Time from Drake Bay</Label>
                            <Input
                              value={departureSansaTime}
                              onChange={(e) => setDepartureSansaTime(e.target.value)}
                              placeholder="e.g., 10:30 AM"
                              className="bg-white"
                              data-testid="departure-sansa-time"
                            />
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-100 border border-blue-300">
                              <input
                                type="checkbox"
                                id="departure-sansa-taxi-checkbox"
                                checked={departureSansaTaxiRequested}
                                onChange={(e) => setDepartureSansaTaxiRequested(e.target.checked)}
                                className="rounded border-blue-400"
                              />
                              <label htmlFor="departure-sansa-taxi-checkbox" className="text-sm text-blue-800 cursor-pointer">
                                Request taxi pickup to airport ($60 paid to driver)
                              </label>
                            </div>
                            {departureSansaTaxiRequested && (
                              <div className="space-y-2">
                                <Label>Taxi Pickup Time at Cabin</Label>
                                <Input
                                  value={departureSansaTaxiTime}
                                  onChange={(e) => setDepartureSansaTaxiTime(e.target.value)}
                                  placeholder="e.g., 9:00 AM"
                                  className="bg-white"
                                  data-testid="departure-sansa-taxi-time"
                                />
                                <p className="text-xs text-muted-foreground">
                                  We recommend arriving at the airport 30-45 minutes before your flight.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {departureMethod === "boat_shuttle" && (
                        <div className="p-4 rounded-lg border border-blue-200 bg-blue-50/50 space-y-4">
                          <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                            <Ship className="h-4 w-4" />
                            Boat Shuttle from Drake Bay to Sierpe
                          </h4>
                          <p className="text-sm text-blue-700">
                            Boat shuttles are $20/person paid directly to the boat captain. We'll make the reservation.
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            <div
                              onClick={() => {
                                setDepartureShuttleTime("7:30am");
                                setDepartureTaxiTime("6:45 AM");
                              }}
                              className={cn(
                                "p-4 rounded-lg border cursor-pointer transition-all text-center",
                                departureShuttleTime === "7:30am"
                                  ? "bg-white border-blue-400 shadow-md"
                                  : "bg-white/50 border-blue-200 hover:border-blue-300"
                              )}
                              data-testid="departure-shuttle-730"
                            >
                              <p className="font-semibold">7:30 AM</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Taxi at cabin by 6:45 AM ($30)
                              </p>
                            </div>
                            <div
                              onClick={() => {
                                setDepartureShuttleTime("2:30pm");
                                setDepartureTaxiTime("1:45 PM");
                              }}
                              className={cn(
                                "p-4 rounded-lg border cursor-pointer transition-all text-center",
                                departureShuttleTime === "2:30pm"
                                  ? "bg-white border-blue-400 shadow-md"
                                  : "bg-white/50 border-blue-200 hover:border-blue-300"
                              )}
                              data-testid="departure-shuttle-230"
                            >
                              <p className="font-semibold">2:30 PM</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Taxi at cabin by 1:45 PM ($30)
                              </p>
                            </div>
                          </div>
                          
                          {departureShuttleTime && (
                            <>
                              <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-100 border border-blue-300">
                                <input
                                  type="checkbox"
                                  id="departure-taxi-checkbox"
                                  checked={departureTaxiRequested}
                                  onChange={(e) => setDepartureTaxiRequested(e.target.checked)}
                                  className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                                  data-testid="departure-taxi-checkbox"
                                />
                                <label htmlFor="departure-taxi-checkbox" className="text-sm text-blue-800">
                                  Yes, please arrange a taxi from the cabin to Drake Bay
                                </label>
                              </div>
                              
                              {departureTaxiRequested && (
                                <div className="p-3 rounded-lg bg-white border border-blue-200">
                                  <Label className="text-sm text-blue-800 mb-2 block">
                                    Taxi pickup time (default based on shuttle):
                                  </Label>
                                  <Input
                                    value={departureTaxiTime}
                                    onChange={(e) => setDepartureTaxiTime(e.target.value)}
                                    placeholder="e.g., 6:45 AM"
                                    className="bg-white border-blue-200"
                                    data-testid="departure-taxi-time"
                                  />
                                  <p className="text-xs text-muted-foreground mt-2">
                                    Taxi cost is $30, paid directly to driver.
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Checkout Time */}
                    <div className="space-y-2">
                      <Label>Checkout Time (Optional)</Label>
                      <Input
                        value={checkoutTime}
                        onChange={(e) => setCheckoutTime(e.target.value)}
                        placeholder="e.g., 10:00 AM (if known)"
                        className="bg-background/50"
                        data-testid="checkout-time-input"
                      />
                    </div>
                  </>
                )}

                <div className="space-y-3">
                  <Label>{t.dietaryRequirements}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t.dietaryDescription}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {DIETARY_OPTIONS.map((option) => (
                      <div
                        key={option.id}
                        className="flex items-center gap-2 p-3 rounded-md border bg-background/50 border-border"
                      >
                        <Label htmlFor={`dietary-${option.id}`} className="flex-1 text-sm">
                          {option.label}
                        </Label>
                        <Input
                          id={`dietary-${option.id}`}
                          type="number"
                          min={0}
                          max={guestCount || 1}
                          value={dietaryCounts[option.id]}
                          onChange={(e) => updateDietaryCount(option.id, e.target.value)}
                          onBlur={(e) => handleDietaryBlur(option.id, e.target.value)}
                          className="w-16 h-8 text-center bg-background/50"
                          data-testid={`dietary-${option.id}-count`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="specialRequests">{t.specialRequests}</Label>
                  <Textarea
                    id="specialRequests"
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    placeholder={t.specialRequestsPlaceholder}
                    className="bg-background/50 resize-none"
                    rows={2}
                    data-testid="special-requests-input"
                  />
                </div>

                <Button
                  onClick={handleSaveGuest}
                  disabled={submitting}
                  className="w-full bg-primary hover:bg-primary/90"
                  data-testid="save-guest-btn"
                >
                  {guest ? t.updateInfo : t.register}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== TOURS TAB (BBB ONLY) ==================== */}
          {isBirdBrain && (
            <TabsContent value="tours" className="space-y-6">
              {!guest ? (
                <Card className="bg-card border-border shadow-card">
                  <CardContent className="pt-6 text-center py-12">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-display text-lg font-semibold mb-2">Register First</h3>
                    <p className="text-muted-foreground mb-4">Please complete your guest information to browse tours.</p>
                    <Button onClick={() => setActiveTab("guest-info")} data-testid="goto-guest-info">
                      Go to Guest Info
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Tours Sub-Tabs */}
                  <Tabs value={toursSubTab} onValueChange={setToursSubTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 h-auto p-2 gap-2 bg-card/80 backdrop-blur-sm border border-border rounded-xl shadow-sm mb-6">
                      <TabsTrigger 
                        value="my-tours" 
                        className={cn(
                          "flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all duration-200",
                          "hover:bg-primary/10 hover:scale-[1.02]",
                          "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=active]:scale-[1.02]",
                          "data-[state=inactive]:text-muted-foreground"
                        )}
                        data-testid="subtab-my-tours"
                      >
                        <ClipboardList className="w-5 h-5" />
                        <span className="text-sm sm:text-base">{translations[language].tabs.myTours}</span>
                        {tourReservations.length > 0 && (
                          <Badge variant="secondary" className="ml-1 bg-background/50 text-xs">
                            {tourReservations.length}
                          </Badge>
                        )}
                      </TabsTrigger>
                      <TabsTrigger 
                        value="available-tours" 
                        className={cn(
                          "flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all duration-200",
                          "hover:bg-primary/10 hover:scale-[1.02]",
                          "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=active]:scale-[1.02]",
                          "data-[state=inactive]:text-muted-foreground"
                        )}
                        data-testid="subtab-available-tours"
                      >
                        <Compass className="w-5 h-5" />
                        <span className="text-sm sm:text-base">{translations[language].tabs.availableTours}</span>
                      </TabsTrigger>
                    </TabsList>

                    {/* My Tours Sub-Tab */}
                    <TabsContent value="my-tours" className="space-y-4">
                      {tourReservations.length === 0 ? (
                        <Card className="bg-card border-border shadow-card">
                          <CardContent className="pt-6 text-center py-12">
                            <Compass className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-display text-lg font-semibold mb-2">No Tours Booked Yet</h3>
                            <p className="text-muted-foreground mb-4">Browse available tours to book your next adventure!</p>
                            <Button onClick={() => setToursSubTab("available-tours")} data-testid="goto-available-tours">
                              <Compass className="w-4 h-4 mr-2" />
                              Browse Tours
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="space-y-4">
                          {tourReservations.map((res) => (
                            <Card 
                              key={res.id}
                              className="bg-card border-border shadow-card border-l-4 border-l-primary"
                              data-testid={`tour-reservation-${res.id}`}
                            >
                              <CardContent className="pt-4">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <h4 className="font-semibold text-lg">{res.tour_name}</h4>
                                    <p className="text-muted-foreground">
                                      {format(parse(res.date, "yyyy-MM-dd", new Date()), "EEEE, MMMM d, yyyy")}
                                    </p>
                                  </div>
                                  <Badge className={cn("border", getTourStatusBadge(res.status))}>
                                    {res.status.charAt(0).toUpperCase() + res.status.slice(1)}
                                  </Badge>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                                  <div className="flex items-center gap-2">
                                    <Users className="w-4 h-4 text-muted-foreground" />
                                    <span>{res.num_participants} participants</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                                    <span>${res.total_price.toFixed(2)} total</span>
                                  </div>
                                </div>
                                
                                {res.status === "confirmed" && (
                                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                                    <div className="flex items-start gap-2">
                                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                                      <div>
                                        <p className="font-medium text-green-800">Confirmed!</p>
                                        <p className="text-sm text-green-700">{res.confirmation_message}</p>
                                        <p className="text-sm text-green-700 mt-1">
                                          <Clock className="w-3 h-3 inline mr-1" />
                                          Pickup: {res.pickup_time}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {res.status === "pending" && (
                                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                                    <div className="flex items-start gap-2">
                                      <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                                      <div>
                                        <p className="font-medium text-yellow-800">Awaiting Confirmation</p>
                                        <p className="text-sm text-yellow-700">
                                          We'll confirm your booking soon.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {res.deposit_required && (
                                  <p className="text-sm text-muted-foreground">
                                    Deposit: ${res.deposit_amount.toFixed(2)} 
                                    {res.deposit_confirmed && <span className="text-green-600 ml-2">✓ Paid</span>}
                                  </p>
                                )}
                                
                                {res.status === "pending" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCancelTour(res.id)}
                                    className="mt-2 text-destructive border-destructive hover:bg-destructive/10"
                                    data-testid={`cancel-tour-${res.id}`}
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Cancel Reservation
                                  </Button>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    {/* Available Tours Sub-Tab */}
                    <TabsContent value="available-tours" className="space-y-4">
                      {/* Tours Grid */}
                      <div className="grid grid-cols-1 gap-4">
                        {tours.map((tour) => (
                          <Card
                            key={tour.id}
                            onClick={() => tour.available && setSelectedTour(selectedTour?.id === tour.id ? null : tour)}
                            className={cn(
                              "cursor-pointer transition-all duration-200",
                              !tour.available && "opacity-50 cursor-not-allowed",
                              selectedTour?.id === tour.id
                                ? "bg-primary/10 border-primary shadow-md ring-2 ring-primary/20"
                                : "bg-card border-border hover:border-primary/50 hover:shadow-sm"
                            )}
                            data-testid={`tour-card-${tour.id}`}
                          >
                            <CardContent className="pt-4">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-semibold text-lg">{tour.name}</h4>
                                <span className="text-primary font-bold text-lg">${tour.price}</span>
                              </div>
                              <p className="text-sm text-muted-foreground mb-3">{tour.description}</p>
                              
                              <div className="flex flex-wrap gap-2 text-xs">
                                <Badge variant="outline" className="bg-background/50">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {tour.duration}
                                </Badge>
                                {tour.lunch_included && (
                                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                                    Lunch included
                                  </Badge>
                                )}
                                {tour.deposit_required && (
                                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                                    ${tour.deposit_amount}{tour.deposit_per_person ? '/person' : ''} deposit
                                  </Badge>
                                )}
                                {tour.requires_passport && (
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                                    <FileText className="w-3 h-3 mr-1" />
                                    Passport required
                                  </Badge>
                                )}
                                {tour.requires_certification && (
                                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">
                                    <FileText className="w-3 h-3 mr-1" />
                                    Certification required
                                  </Badge>
                                )}
                              </div>
                              
                              {!tour.available && (
                                <Badge className="mt-2 bg-red-100 text-red-700">
                                  Currently Unavailable
                                </Badge>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>

                      {/* Booking Form - Shows when tour is selected */}
                      {selectedTour && (
                        <Card className="bg-card border-border shadow-card border-t-4 border-t-primary" data-testid="booking-form-card">
                          <CardHeader>
                            <CardTitle className="font-display text-xl flex items-center gap-2">
                              <MapPin className="w-5 h-5 text-primary" />
                              Book: {selectedTour.name}
                            </CardTitle>
                            <CardDescription>
                              ${selectedTour.price} per person • {selectedTour.duration}
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {/* Date Selection and Number of Participants - Side by Side */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-2">
                                <Label>Tour Date</Label>
                                <Popover open={tourCalendarOpen} onOpenChange={setTourCalendarOpen}>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        "w-full justify-start text-left font-normal bg-background/50",
                                        !tourDate && "text-muted-foreground"
                                      )}
                                      data-testid="tour-date-btn"
                                    >
                                      <CalendarIcon className="mr-2 h-4 w-4" />
                                      {tourDate ? format(tourDate, "PPP") : "Select date"}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={tourDate}
                                      onSelect={(date) => {
                                        setTourDate(date);
                                        setTourCalendarOpen(false);
                                      }}
                                      disabled={(date) => date < startOfDay(addDays(new Date(), 1))}
                                      initialFocus
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>

                              {/* Number of Participants */}
                              <div className="space-y-2">
                                <Label>Participants</Label>
                                <Input
                                  type="number"
                                  min={1}
                                  max={6}
                                  value={numParticipants}
                                  onChange={(e) => setNumParticipants(Math.max(1, Math.min(6, parseInt(e.target.value) || 1)))}
                                  className="bg-background/50"
                                  data-testid="num-participants-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                  1-6 participants
                                </p>
                              </div>
                            </div>

                            {/* Passport Upload (if required) */}
                            {selectedTour.requires_passport && (
                              <div className="space-y-2">
                                <Label>Passport Photos (Required)</Label>
                                <p className="text-xs text-muted-foreground mb-2">
                                  Upload passport photo page for each participant
                                </p>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handlePassportUpload}
                                    disabled={uploadingPassport}
                                    className="bg-background/50"
                                    data-testid="passport-upload-input"
                                  />
                                  {uploadingPassport && <Loader2 className="w-4 h-4 animate-spin" />}
                                </div>
                                {passportFiles.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {passportFiles.map((file, idx) => (
                                      <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                                        <FileText className="w-3 h-3" />
                                        Passport {idx + 1}
                                        <button onClick={() => removePassport(idx)} className="ml-1 hover:text-destructive">
                                          <X className="w-3 h-3" />
                                        </button>
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  {passportFiles.length} of {numParticipants} passports uploaded
                                </p>
                              </div>
                            )}

                            {/* Deposit Confirmation (if required) */}
                            {selectedTour.deposit_required && (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <h4 className="font-medium text-yellow-800 mb-2">
                                  Deposit Required: ${selectedTour.deposit_per_person 
                                    ? (selectedTour.deposit_amount * numParticipants).toFixed(2) + ` ($${selectedTour.deposit_amount}/person × ${numParticipants})`
                                    : selectedTour.deposit_amount.toFixed(2)
                                  }
                                </h4>
                                <p className="text-sm text-yellow-700 mb-2">
                                  Pay via PayPal with an <span className="font-semibold">additional 8% processing fee</span>.
                                </p>
                                <p className="text-sm text-yellow-700 mb-3">
                                  <span className="font-semibold">Total with fee: ${selectedTour.deposit_per_person 
                                    ? ((selectedTour.deposit_amount * numParticipants) * 1.08).toFixed(2)
                                    : (selectedTour.deposit_amount * 1.08).toFixed(2)
                                  }</span>
                                </p>
                                <p className="text-xs text-yellow-600 mb-3">
                                  Deposit is non-refundable. Remaining balance paid on last night of stay.
                                </p>
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    id="deposit-confirmed"
                                    checked={depositConfirmed}
                                    onCheckedChange={setDepositConfirmed}
                                    data-testid="deposit-confirmed-checkbox"
                                  />
                                  <Label htmlFor="deposit-confirmed" className="text-sm cursor-pointer">
                                    I have sent the deposit via PayPal
                                  </Label>
                                </div>
                              </div>
                            )}

                            {/* Certification Upload (for scuba diving) */}
                            {selectedTour.requires_certification && (
                              <div className="space-y-2">
                                <Label>Dive Certification (Required)</Label>
                                <p className="text-xs text-muted-foreground mb-2">
                                  Upload dive certification card for each participant
                                </p>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handlePassportUpload}
                                    disabled={uploadingPassport}
                                    className="bg-background/50"
                                    data-testid="certification-upload-input"
                                  />
                                  {uploadingPassport && <Loader2 className="w-4 h-4 animate-spin" />}
                                </div>
                                {passportFiles.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {passportFiles.map((file, idx) => (
                                      <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                                        <FileText className="w-3 h-3" />
                                        Certification {idx + 1}
                                        <button onClick={() => removePassport(idx)} className="ml-1 hover:text-destructive">
                                          <X className="w-3 h-3" />
                                        </button>
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  {passportFiles.length} of {numParticipants} certifications uploaded
                                </p>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                                  <p className="text-sm text-blue-700">
                                    <span className="font-medium">No certification?</span> Book the "Discovery Dive" instead - no certification required, max depth 12 meters.
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Price Summary */}
                            <div className="bg-primary/10 rounded-lg p-4">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Total Price:</span>
                                <span className="text-xl font-bold text-primary">
                                  ${calculateTourPrice().toFixed(2)}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                ${selectedTour.price} × {numParticipants} participants
                              </p>
                            </div>

                            {/* Submit Button */}
                            <Button
                              onClick={handleBookTour}
                              disabled={tourSubmitting}
                              className="w-full bg-primary hover:bg-primary/90"
                              data-testid="book-tour-btn"
                            >
                              {tourSubmitting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Submitting...
                                </>
                              ) : (
                                <>
                                  <Compass className="w-4 h-4 mr-2" />
                                  Book Tour
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </TabsContent>
          )}

          {/* ==================== RESTAURANT TAB ==================== */}
          <TabsContent value="restaurant" className="space-y-6">
            {/* Restaurant Header */}
            <div className="text-center mb-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">La Olla</h2>
              <p className="text-muted-foreground font-display italic text-lg sm:text-xl">
                {translations[language].landing.slogan}
              </p>
            </div>

            {!guest ? (
              <Card className="bg-card border-border shadow-card">
                <CardContent className="pt-6 text-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-display text-lg font-semibold mb-2">Register First</h3>
                  <p className="text-muted-foreground mb-4">Please complete your guest information to make reservations.</p>
                  <Button onClick={() => setActiveTab("guest-info")} data-testid="goto-guest-info-from-restaurant">
                    Go to Guest Info
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Restaurant Sub-Tabs */}
                <Tabs value={restaurantSubTab} onValueChange={setRestaurantSubTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 h-auto p-2 gap-2 bg-card/80 backdrop-blur-sm border border-border rounded-xl shadow-sm mb-6">
                    <TabsTrigger 
                      value="my-reservations" 
                      className={cn(
                        "flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all duration-200",
                        "hover:bg-primary/10 hover:scale-[1.02]",
                        "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=active]:scale-[1.02]",
                        "data-[state=inactive]:text-muted-foreground"
                      )}
                      data-testid="subtab-my-reservations"
                    >
                      <ClipboardList className="w-5 h-5" />
                      <span className="text-sm sm:text-base">{translations[language].tabs.myReservations}</span>
                      {reservations.length > 0 && (
                        <Badge variant="secondary" className="ml-1 bg-background/50 text-xs">
                          {reservations.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="make-reservation" 
                      className={cn(
                        "flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all duration-200",
                        "hover:bg-primary/10 hover:scale-[1.02]",
                        "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md data-[state=active]:scale-[1.02]",
                        "data-[state=inactive]:text-muted-foreground"
                      )}
                      data-testid="subtab-make-reservation"
                    >
                      <UtensilsCrossed className="w-5 h-5" />
                      <span className="text-sm sm:text-base">{translations[language].tabs.makeReservation}</span>
                    </TabsTrigger>
                  </TabsList>

                  {/* My Reservations Sub-Tab */}
                  <TabsContent value="my-reservations" className="space-y-4">
                    {reservations.length === 0 ? (
                      <Card className="bg-card border-border shadow-card">
                        <CardContent className="pt-6 text-center py-12">
                          <UtensilsCrossed className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="font-display text-lg font-semibold mb-2">No Reservations Yet</h3>
                          <p className="text-muted-foreground mb-4">Make a reservation for your next meal!</p>
                          <Button onClick={() => setRestaurantSubTab("make-reservation")} data-testid="goto-make-reservation">
                            <UtensilsCrossed className="w-4 h-4 mr-2" />
                            Make Reservation
                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-4">
                        {/* Meal Information Card */}
                        <Card className="bg-card border-border shadow-card" data-testid="meal-info-card">
                          <CardHeader className="pb-2">
                            <CardTitle className="font-display text-xl flex items-center gap-2">
                              <UtensilsCrossed className="w-5 h-5 text-primary" />
                              {t.mealTimesAndPrices}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="space-y-2">
                              {Object.entries(meals).map(([key, meal]) => (
                                <div key={key} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                                  <span className="font-medium">{getMealNameTranslation(key, language)}</span>
                                  <span className="text-muted-foreground">
                                    {meal.display_time || `${meal.start_time} - ${meal.end_time}`} • <span className="text-primary font-semibold">${meal.price}</span>
                                  </span>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>

                        {/* Reservations List */}
                        <Card ref={reservationsListRef} className="bg-card border-border shadow-card border-l-4 border-l-primary" data-testid="reservations-list-card">
                          <CardHeader>
                            <CardTitle className="font-display text-xl flex items-center gap-2">
                              <CalendarIcon className="w-5 h-5 text-primary" />
                              {t.yourReservations}
                            </CardTitle>
                            <CardDescription>
                              {reservations.length} {t.reservationsDuring}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              {reservations.map((res) => (
                                <div
                                  key={res.id}
                                  className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-border"
                                  data-testid={`reservation-${res.id}`}
                                >
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium">{getMealNameTranslation(res.meal_type, language)}</span>
                                      {res.room_service && (
                                        <Badge variant="outline" className="text-xs border-info text-info">
                                          <Bell className="w-3 h-3 mr-1" />
                                          {t.roomService}
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      {format(parse(res.date, "yyyy-MM-dd", new Date()), "EEEE, MMMM d, yyyy", { locale: language === "es" ? es : undefined })}
                                    </p>
                                    {res.special_time_request && (
                                      <p className="text-sm text-info flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {res.special_time_request}
                                      </p>
                                    )}
                                    <p className="text-sm text-muted-foreground">
                                      {res.guest_count} {t.guests} • ${res.price.toFixed(2)}
                                    </p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleCancelReservation(res.id)}
                                    className="text-destructive hover:bg-destructive/10"
                                    data-testid={`cancel-reservation-${res.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>

                        {/* Quick action to make another reservation */}
                        <Button 
                          onClick={() => setRestaurantSubTab("make-reservation")} 
                          className="w-full bg-primary hover:bg-primary/90"
                          data-testid="make-another-reservation-btn"
                        >
                          <UtensilsCrossed className="w-4 h-4 mr-2" />
                          Make Another Reservation
                        </Button>
                      </div>
                    )}
                  </TabsContent>

                  {/* Make Reservation Sub-Tab */}
                  <TabsContent value="make-reservation" className="space-y-4">
                    {/* Meal Information Card */}
                    <Card className="bg-card border-border shadow-card" data-testid="meal-info-card-form">
                      <CardHeader className="pb-2">
                        <CardTitle className="font-display text-xl flex items-center gap-2">
                          <UtensilsCrossed className="w-5 h-5 text-primary" />
                          {t.mealTimesAndPrices}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="space-y-2">
                          {Object.entries(meals).map(([key, meal]) => (
                            <div key={key} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                              <span className="font-medium">{getMealNameTranslation(key, language)}</span>
                              <span className="text-muted-foreground">
                                {meal.display_time || `${meal.start_time} - ${meal.end_time}`} • <span className="text-primary font-semibold">${meal.price}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                        <p className="text-sm text-muted-foreground italic text-center pt-2">
                          {t.guestsMayArrive}
                        </p>
                        
                        {/* Policies */}
                        <div className="bg-background/50 rounded-lg p-4 mt-4 space-y-2 text-sm text-muted-foreground">
                          <p>• {t.policyReservations} <span className="text-foreground font-medium">{t.policyHours}</span> {t.policyBefore}</p>
                          <p>• {t.policyRoomService} <span className="text-primary font-medium">+$2.50{t.perPerson}</span>.</p>
                          <p>• {t.policyPayment}</p>
                          <p>• {t.policyAccept} <span className="text-foreground font-medium">{t.policyMethods}</span>.</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Make Reservation Card */}
                    <Card ref={reservationFormRef} className="bg-card border-border shadow-card" data-testid="reservation-form-card">
                      <CardHeader>
                        <CardTitle className="font-display text-xl flex items-center gap-2">
                          <UtensilsCrossed className="w-5 h-5 text-primary" />
                          {t.makeReservation}
                        </CardTitle>
                        <CardDescription>
                          {t.reservationTimeNote}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>{t.selectDate}</Label>
                          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal bg-background/50",
                                  !selectedDate && "text-muted-foreground"
                                )}
                                data-testid="reservation-date-btn"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {selectedDate ? format(selectedDate, "PPP", { locale: language === "es" ? es : undefined }) : t.selectDate}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-card border-border">
                              <Calendar
                                mode="single"
                                selected={selectedDate}
                                onSelect={setSelectedDate}
                                disabled={(date) => {
                                  const today = startOfDay(new Date());
                                  const maxDate = addDays(today, 1);
                                  return date < today || date > maxDate;
                                }}
                                initialFocus
                                data-testid="reservation-calendar"
                              />
                              <div className="p-3 border-t border-border">
                                <Button 
                                  className="w-full bg-primary hover:bg-primary/90"
                                  onClick={() => setCalendarOpen(false)}
                                  disabled={!selectedDate}
                                >
                                  {tCommon.ok}
                                </Button>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>

                        {/* Meal Selection */}
                        <div className="space-y-2">
                          <Label>{t.selectMeals}</Label>
                          <div className="space-y-2">
                            {Object.entries(meals).map(([key, meal]) => (
                              <div
                                key={key}
                                onClick={() => {
                                  if (selectedMeals.includes(key)) {
                                    setSelectedMeals(selectedMeals.filter(m => m !== key));
                                  } else {
                                    setSelectedMeals([...selectedMeals, key]);
                                  }
                                }}
                                className={cn(
                                  "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all",
                                  selectedMeals.includes(key)
                                    ? "bg-primary/20 border-primary shadow-glow"
                                    : "bg-background/50 border-border hover:border-primary/50"
                                )}
                                data-testid={`meal-option-${key}`}
                              >
                                <div className="flex items-center gap-3">
                                  <Checkbox
                                    checked={selectedMeals.includes(key)}
                                    className="pointer-events-none"
                                  />
                                  <div>
                                    <span className="font-medium">{getMealNameTranslation(key, language)}</span>
                                    <p className="text-xs text-muted-foreground">
                                      {meal.display_time || `${meal.start_time} - ${meal.end_time}`}
                                    </p>
                                  </div>
                                </div>
                                <span className="text-primary font-semibold">${meal.price}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Room Service */}
                        <div
                          onClick={() => setRoomService(!roomService)}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all",
                            roomService
                              ? "bg-info/20 border-info"
                              : "bg-background/50 border-border hover:border-info/50"
                          )}
                          data-testid="room-service-option"
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={roomService}
                              className="pointer-events-none"
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <Bell className="w-4 h-4 text-info" />
                                <span className="font-medium">{t.roomService}</span>
                              </div>
                              <p className="text-xs text-muted-foreground">{t.roomServiceDesc}</p>
                            </div>
                          </div>
                          <span className="text-info font-semibold">+$2.50{t.perPerson}</span>
                        </div>

                        {/* Special Time Request */}
                        <div className="space-y-2">
                          <Label>{t.specialTimeRequest}</Label>
                          <Input
                            value={specialTimeRequest}
                            onChange={(e) => setSpecialTimeRequest(e.target.value)}
                            placeholder={t.specialTimePlaceholder}
                            className="bg-background/50"
                            data-testid="special-time-request-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            {t.specialTimeNote}
                          </p>
                        </div>

                        {/* Price Summary */}
                        <div className="bg-accent/50 rounded-lg p-4">
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">{t.totalFor} {guestCount} {t.guests}</span>
                            <span className="text-2xl font-bold text-primary" data-testid="total-price">
                              ${calculatePrice().toFixed(2)}
                            </span>
                          </div>
                          {roomService && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {t.includes} ${(ROOM_SERVICE_FEE * guestCount * selectedMeals.length).toFixed(2)} {t.roomServiceFee}
                            </p>
                          )}
                        </div>

                        <Button
                          onClick={handleMakeReservation}
                          disabled={submitting || selectedMeals.length === 0}
                          className="w-full bg-primary hover:bg-primary/90"
                          data-testid="submit-reservation-btn"
                        >
                          {t.submitReservation}
                        </Button>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </TabsContent>

          {/* ==================== PAYMENT TAB ==================== */}
          <TabsContent value="payment" className="space-y-6">
            <Card className="bg-card border-border shadow-card" data-testid="payment-info-card">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary" />
                  Payment Information
                </CardTitle>
                <CardDescription>
                  How to pay for tours and restaurant meals
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Main Payment Policy */}
                <div className="bg-primary/10 border border-primary/30 rounded-xl p-6 text-center">
                  <DollarSign className="w-10 h-10 text-primary mx-auto mb-3" />
                  <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                    Pay on Your Last Night
                  </h3>
                  <p className="text-muted-foreground">
                    All tours and restaurant charges are paid together on the <span className="font-semibold text-foreground">last night before departure</span>.
                  </p>
                </div>

                {/* What's Included */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-primary" />
                    What's on Your Tab
                  </h4>
                  
                  {isBirdBrain && (
                    <div className="flex items-start gap-3 p-3 bg-background/50 rounded-lg border border-border">
                      <Compass className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium">Tours</p>
                        <p className="text-sm text-muted-foreground">
                          All tour costs (minus any deposits already paid)
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-3 p-3 bg-background/50 rounded-lg border border-border">
                    <UtensilsCrossed className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">Restaurant Meals</p>
                      <p className="text-sm text-muted-foreground">
                        All meals during your stay (Breakfast $12, Lunch $15, Dinner $18)
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-background/50 rounded-lg border border-border">
                    <Bell className="w-5 h-5 text-info mt-0.5" />
                    <div>
                      <p className="font-medium">Room Service</p>
                      <p className="text-sm text-muted-foreground">
                        +$2.50 per person for any room service orders
                      </p>
                    </div>
                  </div>
                </div>

                {/* Accepted Payment Methods */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-primary" />
                    Accepted Payment Methods
                  </h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg border border-border">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-700" />
                      </div>
                      <div>
                        <p className="font-medium">Cash</p>
                        <p className="text-xs text-muted-foreground">USD or Colones</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg border border-border">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-blue-700" />
                      </div>
                      <div>
                        <p className="font-medium">Credit Card</p>
                        <p className="text-xs text-muted-foreground">Visa, Mastercard</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg border border-border">
                      <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                        <span className="text-yellow-700 font-bold text-sm">PP</span>
                      </div>
                      <div>
                        <p className="font-medium">PayPal</p>
                        <p className="text-xs text-muted-foreground">Online payment</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg border border-border">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <span className="text-purple-700 font-bold text-sm">SM</span>
                      </div>
                      <div>
                        <p className="font-medium">Sinpe Móvil</p>
                        <p className="text-xs text-muted-foreground">Costa Rica mobile</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tour Deposits Note */}
                {isBirdBrain && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-yellow-800">Tour Deposits</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          Some tours require a non-refundable deposit paid via PayPal at the time of booking.
                        </p>
                        <p className="text-sm text-yellow-700 mt-1">
                          <span className="font-semibold">PayPal payments include an additional 8% processing fee.</span>
                        </p>
                        <p className="text-sm text-yellow-700 mt-1">
                          The remaining balance is paid on the last night of your stay.
                        </p>
                        <div className="mt-3 pt-3 border-t border-yellow-300">
                          <p className="text-xs text-yellow-600 font-medium">Tours requiring deposits:</p>
                          <ul className="text-xs text-yellow-700 mt-1 space-y-1">
                            <li>• Corcovado (Sirena): $30/person deposit</li>
                            <li>• Corcovado (San Pedrillo): $30/person deposit</li>
                            <li>• Sirena Overnight: $125 deposit</li>
                            <li>• 2 Nights at Sirena: $200 deposit</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Questions */}
                <div className="text-center pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Questions about payment? Ask at the front desk or contact us at <span className="text-primary">info@amayascorcovado.com</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-border text-center text-xs text-muted-foreground space-y-2">
          <div className="flex justify-center gap-4">
            <a href="/terms" className="hover:text-primary">{t.terms}</a>
            <a href="/privacy" className="hover:text-primary">{t.privacy}</a>
            <a href="/contact" className="hover:text-primary">{t.contact}</a>
            <a href="/refund-policy" className="hover:text-primary">{t.refund}</a>
          </div>
          <p>© 2026 La Olla • amayascorcovado.com</p>
        </div>
      </div>
    </div>
  );
}
